"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface LightboxImage {
  id: string;
  url: string | null;
}

interface Props {
  images: readonly LightboxImage[];
  startIndex: number;
  open: boolean;
  onClose: () => void;
}

/**
 * Fullscreen image lightbox with a horizontally-scrollable thumbnail
 * strip at the bottom. Keyboard navigation (← → Esc) and click-outside
 * to dismiss; body scroll locked while open. The active thumbnail
 * auto-scrolls into view when navigation changes.
 *
 * RENDERED VIA PORTAL TO document.body. Any ancestor with `transform`,
 * `filter`, `perspective`, or `will-change: transform` creates a
 * containing block for descendant `position: fixed` elements (CSS
 * spec). Portal-to-body sidesteps the issue entirely.
 */
export function ImageLightbox({ images, startIndex, open, onClose }: Props) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const [index, setIndex] = useState(startIndex);
  const stripRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (open) setIndex(startIndex);
  }, [open, startIndex]);

  const total = images.length;
  const safeIndex = total > 0 ? Math.max(0, Math.min(index, total - 1)) : 0;
  const current = images[safeIndex] ?? null;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % total);
  }, [total]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, goPrev, goNext]);

  useEffect(() => {
    if (!open || !stripRef.current) return;
    const activeTile = stripRef.current.children[safeIndex] as
      | HTMLElement
      | undefined;
    activeTile?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [open, safeIndex]);

  if (!open || !current || !mounted) return null;

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Vista de imagen"
      className="fixed inset-0 z-[60] flex flex-col bg-ink/95 backdrop-blur-sm"
    >
      <div className="flex shrink-0 items-center justify-between px-4 py-3 text-cream-50">
        <p className="text-sm font-medium">
          {safeIndex + 1} / {total}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-cream-50/10 transition hover:bg-cream-50/20"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-2"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {current.url ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={current.url}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <p className="text-sm text-cream-50/70">Imagen no disponible</p>
        )}

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Imagen anterior"
              className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-cream-50/10 text-cream-50 backdrop-blur-sm transition hover:bg-cream-50/20 sm:left-4 sm:h-14 sm:w-14"
            >
              <ChevronLeft className="h-6 w-6" aria-hidden />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Imagen siguiente"
              className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-cream-50/10 text-cream-50 backdrop-blur-sm transition hover:bg-cream-50/20 sm:right-4 sm:h-14 sm:w-14"
            >
              <ChevronRight className="h-6 w-6" aria-hidden />
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <ol
          ref={stripRef}
          className="flex shrink-0 gap-2 overflow-x-auto bg-ink/80 px-3 py-3"
        >
          {images.map((img, i) => {
            const isActive = i === safeIndex;
            return (
              <li key={img.id} className="shrink-0">
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Ver imagen ${i + 1}`}
                  aria-current={isActive ? "true" : undefined}
                  className={`relative h-16 w-16 overflow-hidden rounded-lg ring-2 transition sm:h-20 sm:w-20 ${
                    isActive
                      ? "ring-coral"
                      : "ring-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  {img.url ? (
                    /* eslint-disable-next-line @next/next/no-img-element */
                    <img
                      src={img.url}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-cream-100 text-xs text-ink-500">
                      ?
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
